import axios, { AxiosInstance, CreateAxiosDefaults } from "axios";

import { AdminBetaInviteCodesAPI } from "./modules/admin-beta-invite-codes";
import { AdminOrganizationsAPI } from "./modules/admin-organizations";
import { AdminUsageAPI } from "./modules/admin-usage";
import { AdminUsersAPI } from "./modules/admin-users";
import { AudioTranscriptionAPI } from "./modules/audio-transcription";
import { BillingAPI } from "./modules/billing";
import { CanvasAPI } from "./modules/canvas";
import { ConnectorsAPI } from "./modules/connectors";
import { EventStreamAPI } from "./modules/event-stream";
import { FileRecordsAPI } from "./modules/file-records";
import { FlowMessagesAPI } from "./modules/flow-messages";
import { FlowSchedulesAPI } from "./modules/flow-schedules";
import { FlowSessionAnnotationsAPI } from "./modules/flow-session-annotations";
import { FlowSessionsAPI } from "./modules/flow-sessions";
import { GenaiAPI } from "./modules/genai";
import { GroupsAPI } from "./modules/groups";
import { HarnessCapabilitiesAPI } from "./modules/harness-capabilities";
import { HarnessCredentialsAPI } from "./modules/harness-credentials";
import { HarnessPreferencesAPI } from "./modules/harness-preferences";
import { InboxAPI } from "./modules/inbox";
import { IntegrationConnectionsAPI } from "./modules/integration-connections";
import { IntegrationsAPI } from "./modules/integrations";
import { InvitesAPI } from "./modules/invites";
import { LiveblocksAPI } from "./modules/liveblocks";
import { MCPAPI } from "./modules/mcp";
import { OrganizationAPI } from "./modules/organization";
import { ProxyAPI } from "./modules/proxy";
import { RelationsAPI } from "./modules/relations";
import { ResourceTagsAPI } from "./modules/resource-tags";
import { SearchAPI } from "./modules/search";
import { SkillTemplatesAPI } from "./modules/skill-templates";
import { SkillsAPI } from "./modules/skills";
import { ToolAnalyticsAPI } from "./modules/tool-analytics";
import { ToolConfirmationsAPI } from "./modules/tool-confirmations";
import { TranscriptionStreamAPI } from "./modules/transcription-stream";
import { UserAPI } from "./modules/user";
import { UsersAPI } from "./modules/users";
import { WorkspaceAPI } from "./modules/workspaces";

import { Auth0Client, Auth0ClientOptions } from "@auth0/auth0-spa-js";
import { ToolboxAPI } from "./modules/toolbox";
import { ToolSetsAPI } from "./modules/toolsets";
import { ToolSpecAPI } from "./modules/toolspec";
import { getOrgIdFromToken, getUserIdFromToken } from "./utils";

export interface CohesiveAPIClientOptions {
  /** Required when useAuth0Spa is true (default). Ignored when useAuth0Spa is false and auth0 is not passed. */
  auth0Options?: Auth0ClientOptions;
  axiosOptions?: CreateAxiosDefaults;
  organizationId?: string;
  userId?: string;
  integrationId?: string;
  axios?: AxiosInstance;
  auth0?: Auth0Client;
  /**
   * When false, do not instantiate @auth0/auth0-spa-js (avoids secure-origin checks on non-localhost
   * hosts such as host.docker.internal). Call setGetToken() and/or use bearer interceptors instead.
   * @default true
   */
  useAuth0Spa?: boolean;
}

function createAuth0SpaDisabledStub(): Auth0Client {
  const notAvailable = async (): Promise<string> => {
    throw new Error(
      "Auth0 SPA is disabled for this API client; use setGetToken() or Authorization interceptors.",
    );
  };
  return {
    getTokenSilently: notAvailable,
  } as Auth0Client;
}

export class CohesiveAPIClient {
  auth0Options: Auth0ClientOptions;
  axiosOptions?: CreateAxiosDefaults;
  organizationId?: string;
  userId?: string;
  integrationId?: string;
  useAuth0Spa: boolean;

  auth0: Auth0Client;
  axios: AxiosInstance;

  // API Modules
  adminBetaInviteCodes: AdminBetaInviteCodesAPI;
  adminOrganizations: AdminOrganizationsAPI;
  adminUsage: AdminUsageAPI;
  adminUsers: AdminUsersAPI;
  audioTranscription: AudioTranscriptionAPI;
  billing: BillingAPI;
  canvas: CanvasAPI;
  connectors: ConnectorsAPI;
  flowSessionAnnotations: FlowSessionAnnotationsAPI;
  eventStream: EventStreamAPI;
  fileRecords: FileRecordsAPI;
  flowSchedules: FlowSchedulesAPI;
  flowSessions: FlowSessionsAPI;
  search: SearchAPI;
  genai: GenaiAPI;
  flowMessages: FlowMessagesAPI;
  toolConfirmations: ToolConfirmationsAPI;
  groups: GroupsAPI;
  harnessCapabilities: HarnessCapabilitiesAPI;
  harnessCredentials: HarnessCredentialsAPI;
  harnessPreferences: HarnessPreferencesAPI;
  inbox: InboxAPI;
  integrations: IntegrationsAPI;
  integrationConnections: IntegrationConnectionsAPI;
  invites: InvitesAPI;
  liveblocks: LiveblocksAPI;
  mcp: MCPAPI;
  organization: OrganizationAPI;
  proxy: ProxyAPI;
  relations: RelationsAPI;
  resourceTags: ResourceTagsAPI;
  skills: SkillsAPI;
  skillTemplates: SkillTemplatesAPI;
  toolAnalytics: ToolAnalyticsAPI;
  toolSpec: ToolSpecAPI;
  transcriptionStream: TranscriptionStreamAPI;
  toolSets: ToolSetsAPI;
  toolbox: ToolboxAPI;
  user: UserAPI;
  users: UsersAPI;
  workspaces: WorkspaceAPI;

  getToken: () => Promise<string>;

  constructor(options: CohesiveAPIClientOptions) {
    this.useAuth0Spa = options.useAuth0Spa !== false;
    this.auth0Options = options.auth0Options ?? ({} as Auth0ClientOptions);
    this.axiosOptions = options.axiosOptions;

    this.organizationId = options.organizationId;
    this.integrationId = options.integrationId;
    this.userId = options.userId;

    this.axios = options.axios || axios.create(options.axiosOptions);
    if (options.auth0) {
      this.auth0 = options.auth0;
    } else if (!this.useAuth0Spa) {
      this.auth0 = createAuth0SpaDisabledStub();
    } else {
      if (!options.auth0Options) {
        throw new Error(
          "CohesiveAPIClient: auth0Options is required when useAuth0Spa is true",
        );
      }
      this.auth0 = new Auth0Client(options.auth0Options);
    }

    this.getToken = this._getToken;

    // Initialize API modules
    this.adminBetaInviteCodes = new AdminBetaInviteCodesAPI(this);
    this.adminOrganizations = new AdminOrganizationsAPI(this);
    this.adminUsage = new AdminUsageAPI(this);
    this.adminUsers = new AdminUsersAPI(this);
    this.audioTranscription = new AudioTranscriptionAPI(this);
    this.billing = new BillingAPI(this);
    this.canvas = new CanvasAPI(this);
    this.connectors = new ConnectorsAPI(this);
    this.flowSessionAnnotations = new FlowSessionAnnotationsAPI(this);
    this.eventStream = new EventStreamAPI(this);
    this.fileRecords = new FileRecordsAPI(this);
    this.flowSchedules = new FlowSchedulesAPI(this);
    this.flowSessions = new FlowSessionsAPI(this);
    this.search = new SearchAPI(this);
    this.genai = new GenaiAPI(this);
    this.flowMessages = new FlowMessagesAPI(this);
    this.toolConfirmations = new ToolConfirmationsAPI(this);
    this.groups = new GroupsAPI(this);
    this.harnessCapabilities = new HarnessCapabilitiesAPI(this);
    this.harnessCredentials = new HarnessCredentialsAPI(this);
    this.harnessPreferences = new HarnessPreferencesAPI(this);
    this.inbox = new InboxAPI(this);
    this.integrations = new IntegrationsAPI(this);
    this.integrationConnections = new IntegrationConnectionsAPI(this);
    this.invites = new InvitesAPI(this);
    this.liveblocks = new LiveblocksAPI(this);
    this.mcp = new MCPAPI(this);
    this.organization = new OrganizationAPI(this);
    this.proxy = new ProxyAPI(this);
    this.relations = new RelationsAPI(this);
    this.resourceTags = new ResourceTagsAPI(this);
    this.skills = new SkillsAPI(this);
    this.skillTemplates = new SkillTemplatesAPI(this);
    this.toolAnalytics = new ToolAnalyticsAPI(this);
    this.transcriptionStream = new TranscriptionStreamAPI(this);
    this.toolSpec = new ToolSpecAPI(this);
    this.toolSets = new ToolSetsAPI(this);
    this.toolbox = new ToolboxAPI(this);
    this.user = new UserAPI(this);
    this.users = new UsersAPI(this);
    this.workspaces = new WorkspaceAPI(this);
  }

  org(organizationId: string) {
    return new CohesiveAPIClient({
      auth0Options: this.auth0Options,
      useAuth0Spa: this.useAuth0Spa,
      axiosOptions: this.axiosOptions,
      axios: this.axios,
      auth0: this.auth0,
      organizationId,
      integrationId: this.integrationId,
      userId: this.userId,
    });
  }

  integration(integrationId: string) {
    return new CohesiveAPIClient({
      auth0Options: this.auth0Options,
      useAuth0Spa: this.useAuth0Spa,
      axiosOptions: this.axiosOptions,
      axios: this.axios,
      auth0: this.auth0,
      organizationId: this.organizationId,
      userId: this.userId,
      integrationId,
    });
  }

  uid(userId: string) {
    return new CohesiveAPIClient({
      auth0Options: this.auth0Options,
      useAuth0Spa: this.useAuth0Spa,
      axiosOptions: this.axiosOptions,
      axios: this.axios,
      auth0: this.auth0,
      organizationId: this.organizationId,
      userId: userId,
      integrationId: this.integrationId,
    });
  }

  private async _getToken() {
    const token = await this.auth0.getTokenSilently();
    return token;
  }

  setGetToken(getToken: () => Promise<string>) {
    this.getToken = getToken;
  }

  getIntegrationID() {
    return this.integrationId;
  }

  async getOrgID() {
    if (this.organizationId) {
      return this.organizationId;
    }

    const token = await this.getToken();

    return getOrgIdFromToken(token);
  }

  async getUserID() {
    if (this.userId) {
      return this.userId;
    }

    const token = await this.getToken();
    return getUserIdFromToken(token);
  }
}
